import _ from 'lodash';

import escapeHtml from './escape_html';
import blotFactory from '../components/Builder/Library/Text/Rich/Blots';

export default class DeltaRenderer {
  constructor(value) {
    this.value = value;
    this.Blots = blotFactory();
  }

  reset() {
    this.state = {
      output: [],
      buffer: '',
      container: '',
    };
  }

  render() {
    this.reset();
    this.splitOpsByLines().forEach(({ ops, attributes, block }) => {
      this.setContainerForAttributes(attributes);
      this.state.buffer += this.applyAttributes(
        ops
          .map(op => {
            if (_.isString(op.insert)) {
              return this.applyAttributes(op.insert, op.attributes);
            }
            if (_.isPlainObject(op.insert)) {
              return Object.entries(op.insert || {}).reduce((current, [key, value]) => {
                const blotEmbed = this.Blots.Embeds.named(key);
                const blotBlock = this.Blots.BlockEmbeds.named(key);
                const blot = blotEmbed || blotBlock;
                if (blot && value) {
                  return blot.serverRender(value);
                }
                return current;
              }, '');
            }
            return '';
          })
          .join(''),
        !attributes?.list && !block ? { p: true } : attributes
      );
    });

    this.flush();
    return this.state.output.join('');
  }

  isBlock(blotName) {
    return !!(this.Blots.Blocks.named(blotName) || this.Blots.BlockEmbeds.named(blotName));
  }

  // eslint-disable-next-line class-methods-use-this
  isEmptyHeading(key, value) {
    if (this.Blots.Blocks.named(key)) {
      return value.trim() === '' || value.trim() === '\n' || value.trim() === '<br>';
    }
    return false;
  }

  splitOpsByLines() {
    const lines = [];
    let currentLine = { ops: [] };
    this.value?.ops?.forEach(op => {
      if (_.isString(op.insert)) {
        op = _.cloneDeep(op);
        while (op.insert.length > 0 && op.insert[0] === '\n') {
          // finish prev line with these attributes
          if (currentLine.ops.length === 0) {
            currentLine.ops.push({ insert: '<br>' });
          }
          currentLine.attributes = { ...op.attributes };
          if (Object.keys(currentLine.attributes || {}).filter(x => this.isBlock(x)).length > 0) {
            currentLine.block = true;
          }
          lines.push(currentLine);
          currentLine = { ops: [] };
          op.insert = op.insert.slice(1);
        }
        const splits = op.insert.split('\n');
        splits.forEach((split, index) => {
          const value = escapeHtml(split) || (index > 0 && index < splits.length - 1 ? '<br>' : '');
          if (value) {
            currentLine.ops.push({ insert: value, attributes: index < splits.length - 1 ? {} : op.attributes });
          }
          if (currentLine.ops.length > 0) {
            currentLine.attributes = { ...op.attributes };
            if (Object.keys(currentLine.attributes || {}).filter(x => this.Blots.Blocks.named(x)).length > 0) {
              currentLine.block = true;
            }
            if (currentLine.block || index < splits.length - 1) {
              lines.push(currentLine);
              currentLine = { ops: [] };
            }
          }
        });
      } else if (_.isPlainObject(op.insert)) {
        if (Object.keys(op.insert) > 1) {
          console.warn('more than one operation in insert: ', op.insert);
        }
        const key = Object.keys(op.insert)[0];
        const value = op.insert[key];
        const blockblot = this.Blots.BlockEmbeds.named(key);
        if (blockblot && value) {
          if (currentLine.ops.length > 0) {
            if (Object.keys(currentLine.attributes || {}).filter(x => this.Blots.Blocks.named(x)).length > 0) {
              currentLine.block = true;
            }
            lines.push(currentLine);
            currentLine = { ops: [] };
          }
          currentLine.ops.push(op);
          currentLine.attributes = { ...op.attributes };
          currentLine.block = true;
          lines.push(currentLine);
          currentLine = { ops: [] };
        } else {
          currentLine.ops.push(op);
        }
      }
    });
    if (currentLine.ops.length > 0) {
      if (Object.keys(currentLine.attributes || {}).filter(x => this.Blots.Blocks.named(x)).length > 0) {
        currentLine.block = true;
      }
      lines.push(currentLine);
      currentLine = { ops: [] };
    }
    return lines;
  }

  setContainerForAttributes(attributes) {
    if (attributes && attributes.list) {
      const newContainer = attributes.list === 'bullet' ? 'ul' : 'ol';
      if (newContainer !== this.state.container) {
        this.setContainer(newContainer);
      }
    } else if (!attributes?.list) {
      this.setContainer('');
    }
  }

  setContainer(newContainer) {
    this.flush();
    this.state.container = newContainer;
  }

  applyAttributes(output, attributes) {
    if (!output && attributes?.p) {
      return output;
    }
    return Object.entries(attributes || {}).reduce((current, [key, value]) => {
      switch (key) {
        case 'p':
          return value ? `<p>${current}</p>` : current;
        case 'bold':
          return value ? `<strong>${current}</strong>` : current;
        case 'italic':
          return value ? `<em>${current}</em>` : current;
        case 'underline':
          return value ? `<u>${current}</u>` : current;
        case 'list': {
          const indent = attributes?.indent;
          const indentClass = indent ? `ql-indent-${indent}` : '';
          return `<li class="${indentClass}" data-list="${value}">${current}</li>`;
        }
        case 'link':
          return this.Blots.Inlines.named('link').serverRender(value, current);
        case 'script':
          return value === 'super' ? `<sup>${current}</sup>` : `<sub>${current}</sub>`;
        default: {
          const blot = this.Blots.Blocks.named(key) || this.Blots.Inlines.named(key);
          if (blot && value && !this.isEmptyHeading(key, current)) {
            return `<${blot.tagName.toLowerCase()}${
              blot.className ? ` class="${blot.className}"` : ''
            }>${current}</${blot.tagName.toLowerCase()}>`;
          }
          return current;
        }
      }
    }, output);
  }

  flush() {
    if (this.state.buffer) {
      this.state.output.push(
        this.state.container
          ? `<${this.state.container}>${this.state.buffer}</${this.state.container}>`
          : this.state.buffer
      );
    }
    this.state.buffer = '';
  }
}
